import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/client';
import {
  JobStatusBadge,
  OnboardingStepper,
  RequirementsViewer,
  TemplatePreviewCard,
  SpecUrlList,
  CostTracker,
} from '../../components/onboarding';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../contexts/ToastContext';

export function OnboardingJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [reviewNotes, setReviewNotes] = useState('');
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isUploadingSpec, setIsUploadingSpec] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sourcing: true,
    extraction: true,
    reviewSpecs: true,
    composition: true,
    reviewLabels: true,
    provisioning: true,
    auditTrail: false,
  });

  const { data: job, isLoading } = useQuery({
    queryKey: ['admin', 'onboarding', 'job', jobId],
    queryFn: () => adminApi.onboarding.get(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Poll actively for stages that change automatically
      if (status && ['sourcing', 'extracting', 'composing', 'provisioning'].includes(status)) {
        return 5_000;
      }
      return false;
    },
  });

  // Mutations
  const reviewSpecsMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.onboarding.reviewSpecs>[1]) =>
      adminApi.onboarding.reviewSpecs(jobId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      setReviewNotes('');
      addToast({ type: 'success', message: 'Specs review submitted' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const reviewLabelsMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.onboarding.reviewLabels>[1]) =>
      adminApi.onboarding.reviewLabels(jobId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      setReviewNotes('');
      addToast({ type: 'success', message: 'Labels review submitted' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const retryMutation = useMutation({
    mutationFn: () => adminApi.onboarding.retry(jobId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: 'Retry started' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.onboarding.delete(jobId!),
    onSuccess: () => {
      addToast({ type: 'success', message: 'Job deleted' });
      navigate('/admin/onboarding');
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const advanceMutation = useMutation({
    mutationFn: () => adminApi.onboarding.advance(jobId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: 'Pipeline started — processing in background. Refresh to check progress.' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => adminApi.onboarding.update(jobId!, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      if (variables.status === 'cancelled') {
        addToast({ type: 'success', message: 'Job cancelled' });
        navigate('/admin/onboarding');
      } else {
        addToast({ type: 'success', message: 'Job updated' });
      }
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const handleSpecUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !jobId) return;

    setIsUploadingSpec(true);
    try {
      for (const file of Array.from(files)) {
        await adminApi.onboarding.uploadSpec(jobId, file);
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: `${files.length} spec file(s) uploaded` });
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : 'Upload failed' });
    } finally {
      setIsUploadingSpec(false);
      e.target.value = ''; // Reset file input
    }
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading || !job) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const stageIndex = getStageIndex(job.status);
  const isTerminal = job.status === 'completed' || job.status === 'cancelled';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/onboarding')}
            className="text-gray-400 hover:text-gray-600 text-lg"
          >
            ← 
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{job.partnerName}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <JobStatusBadge status={job.status} />
              {job.industryHint && (
                <span className="text-xs text-gray-500 capitalize">{job.industryHint.replace(/-/g, ' ')}</span>
              )}
              <span className="text-xs text-gray-400">Created {new Date(job.createdAt).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {(job.status === 'queued' || job.status === 'failed') && (
            <label className={`btn btn-secondary text-sm cursor-pointer ${isUploadingSpec ? 'opacity-50 pointer-events-none' : ''}`}>
              {isUploadingSpec ? 'Uploading...' : '📄 Upload Spec'}
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xlsx,.html,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleSpecUpload}
                disabled={isUploadingSpec}
              />
            </label>
          )}
          {job.status === 'queued' && (
            <button className="btn btn-primary text-sm" onClick={() => advanceMutation.mutate()}>
              Start Pipeline
            </button>
          )}
          {job.status === 'failed' && (
            <button className="btn btn-primary text-sm" onClick={() => retryMutation.mutate()}>
              Retry
            </button>
          )}
          {!isTerminal && (
            <button className="btn btn-secondary text-sm" onClick={() => setShowConfirmCancel(true)}>
              Cancel
            </button>
          )}
          {isTerminal && (
            <button
              className="text-sm text-red-600 hover:text-red-800"
              onClick={() => setShowConfirmDelete(true)}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Stage stepper */}
      <div className="bg-white rounded-lg border p-4">
        <OnboardingStepper status={job.status} failedStage={job.failedStage} />
      </div>

      {/* Failed reason banner */}
      {job.status === 'failed' && job.failedReason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-red-800">Pipeline Failed</h3>
          <p className="text-sm text-red-700 mt-1">
            Stage: <strong>{job.failedStage}</strong> — {job.failedReason}
          </p>
        </div>
      )}

      {/* ---------- STAGE PANELS ---------- */}

      {/* Uploaded Specs (shown for queued/failed jobs with pre-uploaded specs) */}
      {(job.status === 'queued' || job.status === 'failed') && job.specBlobPaths?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-blue-800">📄 Uploaded Spec Files ({job.specBlobPaths.length})</h3>
          <ul className="mt-2 space-y-1">
            {job.specBlobPaths.map((path, i) => (
              <li key={i} className="text-sm text-blue-700">
                {path.split('/').pop()}
              </li>
            ))}
          </ul>
          <p className="text-xs text-blue-600 mt-2">These files will be used for extraction when the pipeline starts. Click "Start Pipeline" to begin.</p>
        </div>
      )}

      {/* Sourcing Panel */}
      {stageIndex >= 1 && (
        <CollapsibleSection
          title="Sourcing"
          subtitle={job.sourcingCompletedAt ? `Completed ${new Date(job.sourcingCompletedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.sourcing}
          onToggle={() => toggleSection('sourcing')}
        >
          <SpecUrlList urls={job.specUrls} />
          {job.specBlobPaths.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">{job.specBlobPaths.length} document(s) stored in blob</p>
          )}
        </CollapsibleSection>
      )}

      {/* Extraction Panel */}
      {stageIndex >= 2 && (
        <CollapsibleSection
          title="Extraction"
          subtitle={job.extractionCompletedAt ? `Completed ${new Date(job.extractionCompletedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.extraction}
          onToggle={() => toggleSection('extraction')}
        >
          {job.extractedRequirements ? (
            <RequirementsViewer
              requirements={job.extractedRequirements}
              confidence={job.extractionConfidence}
            />
          ) : (
            <p className="text-sm text-gray-400 italic">Requirements not yet extracted.</p>
          )}
        </CollapsibleSection>
      )}

      {/* Review Specs Gate */}
      {(job.status === 'review_specs' || stageIndex >= 3) && (
        <CollapsibleSection
          title="★ Review Specs"
          subtitle={job.specsReviewedAt ? `Reviewed by ${job.specsReviewedBy} on ${new Date(job.specsReviewedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.reviewSpecs}
          onToggle={() => toggleSection('reviewSpecs')}
          highlight={job.status === 'review_specs'}
        >
          {job.status === 'review_specs' && job.extractedRequirements && (
            <div className="space-y-4">
              <RequirementsViewer
                requirements={job.extractedRequirements}
                confidence={job.extractionConfidence}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes (optional)</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Any notes about the specs..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  className="btn bg-green-600 text-white hover:bg-green-700"
                  disabled={reviewSpecsMutation.isPending}
                  onClick={() => reviewSpecsMutation.mutate({
                    action: 'approve',
                    notes: reviewNotes || undefined,
                    reviewer: 'admin',
                  })}
                >
                  {reviewSpecsMutation.isPending ? 'Submitting...' : '✓ Approve Specs'}
                </button>
                <button
                  className="btn bg-red-600 text-white hover:bg-red-700"
                  disabled={reviewSpecsMutation.isPending}
                  onClick={() => reviewSpecsMutation.mutate({
                    action: 'reject',
                    notes: reviewNotes || 'Rejected',
                    reviewer: 'admin',
                  })}
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          )}
          {job.specsReviewedAt && job.specsReviewNotes && (
            <p className="text-sm text-gray-600 mt-2 italic">"{job.specsReviewNotes}"</p>
          )}
        </CollapsibleSection>
      )}

      {/* Composition Panel */}
      {stageIndex >= 4 && (
        <CollapsibleSection
          title="Composition"
          subtitle={job.compositionCompletedAt ? `Completed ${new Date(job.compositionCompletedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.composition}
          onToggle={() => toggleSection('composition')}
        >
          {job.generatedTemplates.length > 0 ? (
            <div className="space-y-4">
              {job.generatedTemplates.map((tmpl) => (
                <TemplatePreviewCard key={tmpl.templateKey} template={tmpl} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">Templates not yet composed.</p>
          )}
        </CollapsibleSection>
      )}

      {/* Review Labels Gate */}
      {(job.status === 'review_labels' || stageIndex >= 5) && (
        <CollapsibleSection
          title="★ Review Labels"
          subtitle={job.labelsReviewedAt ? `Reviewed by ${job.labelsReviewedBy} on ${new Date(job.labelsReviewedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.reviewLabels}
          onToggle={() => toggleSection('reviewLabels')}
          highlight={job.status === 'review_labels'}
        >
          {job.status === 'review_labels' && (
            <div className="space-y-4">
              {job.generatedTemplates.map((tmpl) => (
                <TemplatePreviewCard key={tmpl.templateKey} template={tmpl} />
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes (optional)</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Any notes about the labels..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  className="btn bg-green-600 text-white hover:bg-green-700"
                  disabled={reviewLabelsMutation.isPending}
                  onClick={() => reviewLabelsMutation.mutate({
                    action: 'approve',
                    notes: reviewNotes || undefined,
                    reviewer: 'admin',
                  })}
                >
                  {reviewLabelsMutation.isPending ? 'Submitting...' : '✓ Approve Labels'}
                </button>
                <button
                  className="btn bg-red-600 text-white hover:bg-red-700"
                  disabled={reviewLabelsMutation.isPending}
                  onClick={() => reviewLabelsMutation.mutate({
                    action: 'reject',
                    notes: reviewNotes || 'Rejected',
                    reviewer: 'admin',
                  })}
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          )}
          {job.labelsReviewedAt && job.labelsReviewNotes && (
            <p className="text-sm text-gray-600 mt-2 italic">"{job.labelsReviewNotes}"</p>
          )}
        </CollapsibleSection>
      )}

      {/* Provisioning Panel */}
      {stageIndex >= 6 && (
        <CollapsibleSection
          title="Provisioning"
          subtitle={job.provisioningCompletedAt ? `Completed ${new Date(job.provisioningCompletedAt).toLocaleString()}` : undefined}
          isOpen={expandedSections.provisioning}
          onToggle={() => toggleSection('provisioning')}
        >
          {job.provisionedRetailerId ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Retailer:</span>
                <a
                  href={`/admin/retailers`}
                  className="text-primary-600 hover:underline font-medium"
                >
                  {job.provisionedRetailerId}
                </a>
              </div>
              {job.provisionedTemplateIds.length > 0 && (
                <div>
                  <span className="text-gray-500">Templates:</span>
                  <ul className="mt-1 space-y-0.5">
                    {job.provisionedTemplateIds.map((id) => (
                      <li key={id} className="text-primary-600 text-xs">{id}</li>
                    ))}
                  </ul>
                </div>
              )}
              {job.provisionedAssignmentIds.length > 0 && (
                <div>
                  <span className="text-gray-500">Assignments:</span>
                  <span className="ml-2 text-gray-900">{job.provisionedAssignmentIds.length} created</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">Not yet provisioned.</p>
          )}
        </CollapsibleSection>
      )}

      {/* Cost tracker */}
      <CostTracker
        tokensUsed={job.totalTokensUsed}
        apiCalls={job.totalApiCalls}
        estimatedCost={job.estimatedCost}
      />

      {/* Audit Trail */}
      {job.auditLog && job.auditLog.length > 0 && (
        <CollapsibleSection
          title="Audit Trail"
          subtitle={`${job.auditLog.length} entries`}
          isOpen={expandedSections.auditTrail}
          onToggle={() => toggleSection('auditTrail')}
        >
          <div className="space-y-0">
            {[...job.auditLog].reverse().map((entry, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 py-2 text-sm ${i > 0 ? 'border-t border-gray-100' : ''}`}
              >
                <div className="flex-shrink-0 w-40 text-xs text-gray-400 pt-0.5">
                  {new Date(entry.timestamp).toLocaleString()}
                </div>
                <div className="flex-shrink-0">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${stageColor(entry.stage)}`}>
                    {entry.stage}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900">{entry.action}</span>
                  {entry.details && (
                    <span className="text-gray-500 ml-1">— {entry.details}</span>
                  )}
                  {entry.actor && entry.actor !== 'system' && (
                    <span className="text-gray-400 ml-1 text-xs">by {entry.actor}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Confirm Cancel Modal */}
      {showConfirmCancel && (
        <ConfirmModal
          title="Cancel Onboarding?"
          message={`Cancel the onboarding pipeline for "${job.partnerName}"? This action cannot be undone.`}
          confirmLabel="Cancel Job"
          confirmVariant="danger"
          isLoading={updateMutation.isPending}
          onConfirm={() => {
            updateMutation.mutate({ status: 'cancelled' });
            setShowConfirmCancel(false);
          }}
          onCancel={() => setShowConfirmCancel(false)}
        />
      )}

      {/* Confirm Delete Modal */}
      {showConfirmDelete && (
        <ConfirmModal
          title="Delete Job?"
          message={`Permanently delete the onboarding job for "${job.partnerName}"?`}
          confirmLabel="Delete"
          confirmVariant="danger"
          isLoading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ---- Helpers ----

/** Get numeric stage index for section visibility. Returns -1 for non-pipeline states. */
function getStageIndex(status: string): number {
  const map: Record<string, number> = {
    queued: 0,
    sourcing: 1,
    extracting: 2,
    review_specs: 3,
    composing: 4,
    review_labels: 5,
    provisioning: 6,
    completed: 7,
    // Show all panels for failed/cancelled so user can inspect
    failed: 7,
    cancelled: 7,
  };
  return map[status] ?? 0;
}

// ---- Sub-components ----

function CollapsibleSection({
  title,
  subtitle,
  isOpen,
  onToggle,
  highlight,
  children,
}: {
  title: string;
  subtitle?: string;
  isOpen: boolean;
  onToggle: () => void;
  highlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`bg-white rounded-lg border overflow-hidden ${highlight ? 'ring-2 ring-yellow-400' : ''}`}>
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div>
          <span className="text-sm font-bold text-gray-900">{title}</span>
          {subtitle && <span className="ml-2 text-xs text-gray-400">{subtitle}</span>}
        </div>
        <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && <div className="px-5 pb-4">{children}</div>}
    </div>
  );
}

/** Map audit-trail stage names to Tailwind badge colors */
function stageColor(stage: string): string {
  const colors: Record<string, string> = {
    sourcing: 'bg-blue-100 text-blue-700',
    extracting: 'bg-indigo-100 text-indigo-700',
    review_specs: 'bg-yellow-100 text-yellow-700',
    composing: 'bg-purple-100 text-purple-700',
    review_labels: 'bg-orange-100 text-orange-700',
    provisioning: 'bg-teal-100 text-teal-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return colors[stage] || 'bg-gray-100 text-gray-600';
}
